<template>
  <main-large-dialog
    v-bind="$props"
    :is-visible.sync="computedIsVisible"
    :title="titleItem"
    dialog-width="90%"
    min-body-height="400px"
    @agree="saveHandler"
    max-height="95vh"
    no-paddings-y
    no-paddings-x
    :agree-text="agreeButtonText"
    disagree-text="dialog.disagreeClose"
  >
    <template #body>
      <v-tabs v-model="currentTab" ref="tabs" class="fill-height d-flex flex-column" hide-slider color="font">
        <v-tab
          v-for="tab in tabs"
          :key="tab.value"
          v-text="tab.text"
          active-class="activeTab"
          class="font--text"
          style="text-transform: none;"
        >
        </v-tab>
        <v-tabs-items v-model="currentTab" class="fill-height">
          <v-tab-item style="height: inherit">
            <v-row no-gutters class="pt-4 pl-15 justify-center align-center">
              <v-col cols="10">
                <modeling-chart-dialog-scenario-picker
                  :type="type"
                  v-for="(picker, index) in pickers"
                  :is-last="index === pickers.length - 1"
                  v-bind="picker"
                  @addScenarioPicker="addPicker"
                  @input="updatePickerValue($event, index)"
                  :key="picker.id"
                  @removeScenarioPicker="removeScenarioPicker(index, picker.id)"
                  :is-first="index === 0"
                  :selectItems="variableAttributes"
                  :uniqueSelectedParameters="uniqueSelectedParameters"
                  :selectedParameters="selectedParameters"
                  :net-type="netType"
                  :element-label="elementLabel"
                ></modeling-chart-dialog-scenario-picker>
              </v-col>
            </v-row>
            <v-row no-gutters class="justify-center align-center">
              <v-col cols="10">
                <canvas id="chart" aria-label="Hello ARIA World" role="img" height="500px"></canvas>
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item style="height: inherit">
            <v-row no-gutters>
              <v-col cols="12" class="px-5 py-6">
                <ag-grid-vue
                  style="width: auto; height: 500px"
                  class="ag-theme-material border"
                  :gridOptions="gridOptions"
                  headerHeight="48"
                >
                </ag-grid-vue>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </template>
  </main-large-dialog>
</template>

<script>
import Chart from 'chart.js';
import { AgGridVue } from 'ag-grid-vue';
import { call, get } from 'vuex-pathify';

import ModelingChartDialogScenarioPicker from '@/components/ModelingChartDialogScenarioPicker';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'ModelingChartDialog',
  components: {
    AgGridVue,
    MainLargeDialog,
    ModelingChartDialogScenarioPicker,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    scenarioId: {
      type: [Number, String],
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    elementLabel: {
      type: [Number, String],
      default: null,
    },
    attributes: {
      type: Array,
      required: true,
    },
    netType: {
      type: String,
      required: true,
    },
  },
  data: vm => ({
    pickers: [],
    isConfirmButtonLoading: false,
    isUploading: false,
    currentTab: 0,
    tabs: [
      {
        text: vm.$i18n.t('dialog.body.chart'),
        value: 'web',
      },
      {
        text: vm.$i18n.t('dialog.body.table'),
        value: 'qgis',
      },
    ],
    maxXValue: 24,
  }),
  computed: {
    models: get('modeling/models@:netType'),
    scenarios: get('modeling/modelsScenarios@:netType'),
    selectedScenario: get('modeling/modelsScenarios@:netType@:scenarioId'),
    elements: get('modeling/scenariosElementsByName@:netType'),
    notFoundedElements: get('modeling/scenariosNotFoundedElementsByName@:netType'),
    gridOptions() {
      return {
        rowData: [],
        columnDefs: [],
        pagination: false,
        rowSelection: false,
        rowDeselection: true,
        onGridReady: this.onGridReady,
        defaultColDef: {
          resizable: true,
        },
        localeText: {
          loadingOoo: this.$i18n.t('map.loadingTableData'),
          noRowsToShow: this.$i18n.t('map.noData'),
        },
      };
    },
    layerName() {
      return this.$i18n.t(`sidebar.${this.type}`);
    },
    titleItem() {
      return `${this.layerName}: ${this.elementLabel}`;
    },
    variableAttributes() {
      return this.getParameterSelectItems(
        this.attributes.filter(attribute => !attribute.isConstant && !attribute.isValueQueryEval)
      );
    },
    variablesUnits() {
      return this.$_arrayToObject(this.variableAttributes, 'value', 'unit');
    },
    agreeButtonText() {
      return this.currentTab === 0 ? 'dialog.downloadChart' : 'dialog.downloadTable';
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    modelsArray() {
      return this.$_objectToArray(this.models);
    },
    computedModels() {
      return this.modelsArray.map(model => {
        return {
          ...model,
          key: `models_${model.id}`,
          scenarios: model.scenarios.map(scenario => {
            return { ...scenario, key: `scenarios_${scenario.id}` };
          }),
        };
      });
    },
    uniqueSelectedParameters() {
      return Array.from(new Set(this.selectedParameters));
    },
    selectedParameters() {
      return this.pickers.map(picker => picker.value.parameter).filter(parameter => parameter);
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(nV) {
        if (nV) {
          this.onInit();
        } else {
          this.resetDialog();
        }
      },
    },
  },
  methods: {
    getNetElementValuesByName: call('modeling/getNetElementValuesByName'),
    onGridReady() {
      this.updateGrid();
    },
    getColumnDefs() {
      const columns = this.pickers
        .filter(
          picker =>
            picker.value.modelId === this.selectedScenario.model && picker.value.scenarioId && picker.value.parameter
        )
        .map(picker => {
          const { scenarioId, parameter } = picker.value;
          const unit = this.variablesUnits[parameter];
          return {
            headerName: this.getLabel(scenarioId, parameter, unit),
            field: `${parameter}_${scenarioId}`,
          };
        });
      return [
        {
          headerName: this.$i18n.t('dialog.body.datetime'),
          field: 'timestep',
        },
        ...columns,
      ];
    },
    setColumnDefs(columnsDefs) {
      this.gridOptions.api.setColumnDefs(columnsDefs);
    },
    updateGrid() {
      if (this.gridOptions.api) {
        this.setColumnDefs(this.getColumnDefs());
        this.gridOptions.api.setRowData(this.getRowData());
      }
    },
    getRowData() {
      return this.selectedScenario.timesteps.map((timestep, index) => {
        const computedObject = this.pickers
          .filter(
            picker =>
              picker.value.modelId === this.selectedScenario.model && picker.value.scenarioId && picker.value.parameter
          )
          .reduce((total, picker) => {
            const { parameter, scenarioId } = picker.value;
            return {
              ...total,
              [`${parameter}_${scenarioId}`]: this.elements[scenarioId][this.type][this.elementLabel][parameter][index],
            };
          }, {});
        return {
          timestep: this.$dayjs(timestep).format('L, LT'),
          ...computedObject,
        };
      });
    },
    formatDate(timestep) {
      return this.$dayjs(timestep).format('L, LT');
    },
    getXAxes() {
      const { timesteps } = this.selectedScenario;
      const length = timesteps.length;
      const firstStep = this.$dayjs.utc(timesteps[0]);
      const lastStep = this.$dayjs.utc(timesteps.at(-1));
      const end = lastStep.diff(firstStep, 'h');
      this.maxXValue = end;
      const step = end / (length - 1);
      return {
        id: 'bottom',
        type: 'linear',
        position: 'bottom',
        min: 0,
        max: end,
        ticks: {
          stepSize: step,
        },
        scaleLabel: {
          display: true,
          labelString: this.$i18n.t('sidebar.timeHoursUnit'),
        },
      };
    },
    getParameterSelectItems(attributes) {
      return attributes.map(attr => {
        const { attribute: attributeName, unit } = attr;
        return {
          text: this.$i18n.t(`sidebar.${attributeName}`),
          value: attributeName,
          unit: unit,
        };
      });
    },
    getLabel(scenarioId, parameter, unit) {
      return (
        `${this.scenarios[scenarioId].name} - ${this.$i18n.t(`sidebar.${parameter}`).toLowerCase()}` +
        (unit ? ` [${unit}]` : '')
      );
    },
    getYAxes() {
      return this.chart.options.scales.yAxes;
    },
    getYAxis(side, unit) {
      return {
        id: side,
        type: 'linear',
        position: side,
        scaleLabel: {
          display: true,
          labelString: unit ? ` [${unit}]` : '',
        },
      };
    },
    getYAxisId(parameter, unit) {
      const side = this.uniqueSelectedParameters.indexOf(parameter) === 0 ? 'left' : 'right';
      this.updateYAxes(side, unit);
      return side;
    },
    updateYAxes(side, unit) {
      if (this.getYAxes().length === 1 && this.uniqueSelectedParameters.length === 2) {
        this.addYAxis(side, unit);
      } else if (this.getYAxes().length === 2 && this.uniqueSelectedParameters.length === 1) {
        this.removeRightAxis();
      } else {
        const index = this.getYAxes()
          .map(axis => axis.id)
          .indexOf(side);
        this.getYAxes()[index] = this.getYAxis(side, unit);
      }
    },
    addYAxis(side, unit) {
      this.getYAxes().push(this.getYAxis(side, unit));
    },
    getDataset(data, parameter, color, id) {
      const { timesteps } = this.scenarios[data.scenario];
      const { scenario } = data;
      const datasetData = data[parameter]
        .map((value, index) => {
          return {
            x: this.$dayjs.utc(timesteps[index]).diff(this.$dayjs.utc(timesteps[0]), 'h'),
            y: value,
          };
        })
        .filter(value => value.x <= this.maxXValue);
      const unit = this.variablesUnits[parameter];
      return {
        id,
        label: this.getLabel(scenario, parameter, unit),
        data: [...datasetData],
        fill: false,
        borderColor: color,
        unit: unit,
        borderWidth: 1,
        yAxisID: this.getYAxisId(parameter, unit),
      };
    },
    removeRightAxis() {
      this.getYAxes().splice(1, 1);
    },
    drawChart() {
      const canvas = document.getElementById('chart');
      // https://stackoverflow.com/questions/43664722/how-to-save-chart-js-charts-as-image-without-black-background-using-blobs-and-fi
      Chart.plugins.register({
        afterRender: function(c) {
          const ctx = c.chart.ctx;
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, c.chart.width, c.chart.height);
          ctx.restore();
        },
      });
      this.chart = new Chart(canvas, {
        options: {
          tooltips: {
            callbacks: {
              label: (tooltipItems, data) => {
                const { value, datasetIndex } = tooltipItems;
                const dataset = data.datasets[datasetIndex];
                return value + (dataset.unit ? ` [${dataset.unit}]` : '');
              },
              title: tooltipItems => {
                return tooltipItems.map(element => `${element.label} [h]`);
              },
            },
          },
          maintainAspectRatio: false,
          scales: {
            yAxes: [
              {
                id: 'left',
                type: 'linear',
                position: 'left',
              },
            ],
            xAxes: [this.getXAxes()],
          },
        },
        type: 'line',
        data: {
          datasets: [],
        },
      });
    },
    resetDialog() {
      this.currentTab = 0;
      this.isUploading = false;
      this.pickers = [];
      this.chart.destroy();
      this.chart = undefined;
    },
    async getElement(scenarioId) {
      try {
        const { elementLabel, type, netType } = this;
        if (this.notFoundedElements?.[scenarioId]?.[type]?.includes(elementLabel)) {
          // Element with the given label was not found in the scenario before,
          // so now there is not even an attempt to fetch it
          return;
        }
        await this.getNetElementValuesByName({ scenarioId, elementLabel, type, netType });
      } catch {
        //
      }
    },
    updateChartDataset(dataset, id) {
      const data = this.chart.data.datasets.find(ds => ds.id === id);
      if (!data) {
        this.chart.data.datasets.push(dataset);
      } else {
        Object.assign(data, dataset);
      }
      this.chart.update();
    },
    addChartDataset(dataset) {
      this.chart.data.datasets.push(dataset);
      this.chart.update();
    },
    removeChartDataset(id) {
      const index = this.chart.data.datasets.map(ds => ds.id).indexOf(id);
      // dodany picker, ale jeszcze nie narysowano wykresu
      if (index === -1) {
        return;
      }
      this.chart.data.datasets.splice(index, 1);
      this.updateYAxes();
      this.chart.update();
    },
    removeScenarioPicker(index, id) {
      this.removeChartDataset(id);
      this.pickers.splice(index, 1);
      this.updateGrid();
    },
    async updatePickerValue(data, index) {
      const { value, id } = data;
      const { color, parameter, scenarioId, modelId } = value;
      this.$set(this.pickers[index], 'value', value);
      if (modelId && scenarioId && parameter) {
        let element = this.elements?.[scenarioId]?.[this.type]?.[this.elementLabel];
        if (!element) {
          // Try to fetch element and check if attempt has not yet been made
          await this.getElement(scenarioId, id);
          element = this.elements?.[scenarioId]?.[this.type]?.[this.elementLabel];
          if (!element) {
            // There is not element, so remove its chart dataset and don't add new one
            this.removeChartDataset(id);
            return;
          }
        }
        // There is element, so prepare dataset and add it to chart
        const dataset = this.getDataset(element, parameter, color, id);
        this.updateChartDataset(dataset, id);
        this.updateGrid();
      }
    },
    addPicker() {
      this.$set(this.pickers, this.pickers.length, {
        id: this.$dayjs().valueOf(),
        value: {
          color: this.$_getRandomColor(),
          modelId: this.pickers.length === 0 ? this.selectedScenario?.model || null : undefined,
          scenarioId: this.pickers.length === 0 ? this.scenarioId : undefined,
          parameter: undefined,
        },
      });
    },
    async onInit() {
      await this.$nextTick();
      this.drawChart();
      this.addPicker();
    },
    saveHandler() {
      if (this.currentTab === 0) {
        this.$_saveFile(this.chart.toBase64Image(), this.$i18n.t('dialog.body.chart'));
      } else {
        this.gridOptions.api.exportDataAsCsv({
          columnSeparator: ';',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../node_modules/ag-grid-community/styles/ag-grid.css';
@import '../../node_modules/ag-grid-community/styles/ag-theme-material.css';

::v-deep {
  .ag-center-cols-container {
    width: auto !important;
  }
  .v-input--selection-controls__input .v-icon {
    font-size: 19px;
  }
  .v-slide-group {
    z-index: 99;
    position: sticky;
    top: 0;
    box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),
      0px 1px 10px 0px rgba(0, 0, 0, 0.12) !important;
  }
}

.activeTab {
  background-color: rgba(207, 207, 207, 0.25);
}

.border {
  @include border();
}
</style>
